/**
 * What a History row of an analyst's mark says (PRD-0075 R-C3, R-C4, ADR-0093): "dob set to 1988
 * by Jana", "city (Brno) no longer valid since 1 Jan 2023 — Moved (Jana)", "WORKS_FOR · ACME no
 * longer valid since …", "Marked wrong — … (Jana)", and each undo. A relationship names the
 * entity on its other side, seen from the history it is in.
 */
import type { TFunction } from 'i18next'
import type { CaseEventKind } from '@/services/api/case-events'
import { formatDate } from '@/utils/date'
import { type HistoryActor, historyActorText } from './identity-row-text'

const ROWS = 'investigations.entityDetail.history.rows'

/** An analyst's mark as a history row shows it (`fact_asserted`, `fact_invalidated`, `entity_retired`). */
export interface FactChangeRow {
  target: 'entity' | 'property' | 'relationship'
  /** The property asserted or marked. */
  key: string | null
  /** The value asserted, or the value of the property marked. */
  value: string | null
  /** No longer valid from (YYYY-MM-DD). */
  date: string | null
  /** Why: the reason of an invalidation, the note of a retirement. */
  note: string | null
  /** A relationship's type and the entity on its other side. */
  relationship: { type: string; otherName: string | null } | null
  reverted: boolean
  /** Retired (or restored) along with the entity it was mined from. */
  along: boolean
  actor: HistoryActor
}

export interface FactRowText {
  kind: CaseEventKind | null
  fact?: FactChangeRow | null
}

function invalidatedText(fact: FactChangeRow, values: Record<string, string>, t: TFunction) {
  const on = fact.target === 'relationship' ? 'relationship' : 'property'
  return t(`${ROWS}.${on}${fact.reverted ? 'Revalidated' : 'Invalidated'}`, values)
}

function retiredText(fact: FactChangeRow, values: Record<string, string>, t: TFunction) {
  if (fact.along) return t(`${ROWS}.${fact.reverted ? 'restoredAlong' : 'retiredAlong'}`, values)
  const on = fact.target === 'relationship' ? 'relationship' : 'entity'
  return t(`${ROWS}.${on}${fact.reverted ? 'Restored' : 'Retired'}`, values)
}

/** The row's wording; null for a row that is no analyst mark (an extraction's retirement reads as its label). */
export function factRowText(row: FactRowText, t: TFunction, language: string): string | null {
  const fact = row.fact
  if (!fact) return null
  const values = {
    key: fact.key ?? '',
    value: fact.value ?? t('investigations.entityDetail.history.header.noValue'),
    date: fact.date ? formatDate(fact.date, language) : '',
    note: fact.note ?? '',
    type: fact.relationship?.type ?? '',
    other:
      fact.relationship?.otherName ?? t('investigations.entityDetail.history.identity.unnamed'),
    actor: historyActorText(fact.actor, t),
  }
  switch (row.kind) {
    case 'fact_asserted':
      return t(`${ROWS}.factAsserted`, values)
    case 'fact_invalidated':
      return invalidatedText(fact, values, t)
    case 'entity_retired':
      return retiredText(fact, values, t)
    default:
      return null
  }
}
